// One config object per flagship line so the use-case pages, hub extras and
// home spotlight are shared between roll labels and large format.
import { LABEL_STOCKS, LABEL_USE_CASES } from "./labelUseCases.mjs";
import { LF_STOCKS, LF_USE_CASES } from "./largeFormatUseCases.mjs";

const productPath = (slug) => `/shop/product/${slug}`;

export const LABELS_COLLECTION = {
  key: "labels",
  basePath: "/labels",
  hubPath: "/shop/roll-labels",
  hubName: "Roll Labels",
  productPath,
  stocks: LABEL_STOCKS,
  useCases: LABEL_USE_CASES,
  badges: [
    { key: "waterproof", icon: "droplets", yes: "Waterproof", no: "Dry use", onClass: "text-sky-600", chipClass: "bg-sky-50 text-sky-700" },
    { key: "writable", icon: "pen", yes: "Writable", no: "Laminated", onClass: "text-emerald-600", chipClass: "bg-emerald-50 text-emerald-700" },
  ],
  stockNoun: "label stock",
  tableHead: "Label stock",
  gridOverline: "Labels by what you sell",
  gridTitle: "Find the right label for your product",
  tableOverline: "Compare our nine label stocks",
  tableTitle: "Which label material do I need?",
  recommendedTitle: "Recommended label stocks",
  sizesTitle: "Common sizes",
  moreTitle: "More labels by use",
  sidebarTitle: "Labels by use",
  allLinkLabel: "All roll labels",
  sizeTool: { path: "/tools/label-size", label: "Not sure? Use the label size finder" },
  ctaBlurb: "250 labels minimum, price drops at 1,000 and 5,000. Free digital proof on every order. Printed in Houston, shipped nationwide.",
  keywordSuffix: "roll labels, product labels",
  hero: {
    overline: "Custom labels by use",
    titleA: "The right label for ",
    titleB: "what you sell.",
    plainTitle: "The right label for what you sell.",
    subtitle: "Every product has its own enemies: heat, oil, ice, steam or a state inspector. Pick yours and we'll show the stocks that hold up, the sizes that fit and the details that keep you compliant.",
    seoTitle: "Custom Labels by Product Type — Candle, Bottle, Cosmetic, Food & More | Kolt Printing",
    seoDescription: "Pick your product and see the right label stock, common sizes and pro tips: candle labels, bottle labels, cosmetic labels, food labels, wine and beer, soap, packaging and cannabis. 250 minimum, ships nationwide.",
    keywords: "custom labels, product labels, candle labels, bottle labels, cosmetic labels, food labels, wine labels, soap labels, cannabis labels",
  },
  spotlight: {
    icon: "tag",
    overline: "Our flagship product",
    titleA: "Custom roll labels, ",
    titleB: "printed to order.",
    blurb: "Nine label stocks from economical paper to waterproof BOPP, clear, gold, silver, holographic and UL-certified polyester. Any size from 0.5\", any shape, permanent or removable adhesive, 250-piece minimum with live pricing. Printed in Houston, shipped nationwide.",
    shopLabel: "Shop roll labels",
    toolLabel: "Label size finder",
    toolPath: "/tools/label-size",
    chipsLabel: "Labels by what you sell",
  },
};

export const LARGE_FORMAT_COLLECTION = {
  key: "large-format",
  basePath: "/large-format",
  hubPath: "/shop/large-format",
  hubName: "Large Format",
  productPath,
  stocks: LF_STOCKS,
  useCases: LF_USE_CASES,
  badges: [
    { key: "outdoor", icon: "sun", yes: "Outdoor-rated", no: "Indoor", onClass: "text-amber-600", chipClass: "bg-amber-50 text-amber-700" },
    { key: "hardware", icon: "wrench", yes: "Hardware included", no: "Graphic only", onClass: "text-emerald-600", chipClass: "bg-emerald-50 text-emerald-700" },
  ],
  stockNoun: "display",
  tableHead: "Display / material",
  gridOverline: "Signage by where it works",
  gridTitle: "Find the right display for your event or space",
  tableOverline: "Compare our large format lineup",
  tableTitle: "Which banner, display or sign do I need?",
  recommendedTitle: "Recommended displays and materials",
  sizesTitle: "Common sizes",
  moreTitle: "More signage by use",
  sidebarTitle: "Signage by use",
  allLinkLabel: "All large format",
  sizeTool: { path: "/tools/banner-calculator", label: "Not sure? Use the banner size calculator" },
  ctaBlurb: "Live per-square-foot pricing, hardware included where shown. Free digital proof on every order. Printed in Houston, shipped nationwide — same-day options on vinyl banners.",
  keywordSuffix: "large format printing, banner printing, custom signs",
  hero: {
    overline: "Large format by use",
    titleA: "The right display for ",
    titleB: "where it has to work.",
    plainTitle: "The right display for where it has to work.",
    subtitle: "Trade show floor, festival field, storefront window or construction fence — each one punishes the wrong material. Pick your situation and we'll show the banners, displays and signs that hold up, the sizes that read, and how fast we can ship them.",
    seoTitle: "Large Format Printing by Use — Trade Show Displays, Banners, Backdrops, Signs | Kolt Printing",
    seoDescription: "Custom large format printing for trade shows, events, grand openings, real estate, schools, retail, construction and outdoor activations: retractable banners, vinyl and mesh banners, backdrops, flags, tents, rigid signs. Ships nationwide.",
    keywords: "large format printing, banner printing, trade show displays, retractable banners, step and repeat backdrop, feather flags, yard signs, custom signs",
  },
  spotlight: {
    icon: "flag",
    overline: "Our second flagship line",
    titleA: "Large format printing, ",
    titleB: "built for the real world.",
    blurb: "Retractable banners, vinyl and mesh banners, step and repeat backdrops, tension fabric and SEG displays, flags, tents, table throws, rigid signs and window graphics. Live per-square-foot pricing, hardware included where shown, same-day options on vinyl. Printed in Houston, shipped nationwide.",
    shopLabel: "Shop large format",
    toolLabel: "Banner size calculator",
    toolPath: "/tools/banner-calculator",
    chipsLabel: "Signage by where it works",
  },
};

export const UC_COLLECTIONS = [LABELS_COLLECTION, LARGE_FORMAT_COLLECTION];
export const collectionFor = (basePath) => UC_COLLECTIONS.find((c) => c.basePath === basePath) || null;
export const stockOf = (c, slug) => c.stocks.find((s) => s.slug === slug) || null;
export const findUseCase = (c, slug) => c.useCases.find((u) => u.slug === slug) || null;
export const badgeOn = (stock, badge) => Boolean(stock[badge.key]);
export const cap = (s) => s.replace(/^\w/, (ch) => ch.toUpperCase());
